// #4963 phase 3 — the two referral markers on the synced settings document, and
// the rule that stops a stale device erasing them.
//
// In short: the app cannot read the server-only referrals database, so it has to
// be told it was invited, and the markers are how it remembers. They live on the
// SYNCED settings document because the ordinary invite journey crosses devices.
// That document is last-writer-wins and a claim happens only once, so a marker
// overwritten with `false` would never be written again — hence a merge in both
// directions where `true` always wins, the same shape `review-prompt-settings.ts`
// next door uses for the review-prompt cap.
//
// The full account — why the device's own storage is not enough, what each marker
// arms, and what happens when they disagree with the server — is in
// design-docs/referral-program.md, "How the app knows it was referred".
//
// Angular-free on purpose, so the rule can be driven directly by a test.

/** The settings fields that merge true-wins rather than being assigned. */
export const STICKY_REFERRAL_SETTING_KEYS: ReadonlyArray<string> = [
  /** Somebody invited this user, and their claim succeeded. Arms the training report. */
  'referralWasReferred',
  /** The server has confirmed the training flag is set. Stops this device asking again. */
  'referralTrainingReported',
];

/** Is this one of the fields that merges true-wins rather than being assigned? */
export function isStickyReferralSettingKey(key: string): boolean {
  return STICKY_REFERRAL_SETTING_KEYS.indexOf(key) >= 0;
}

/**
 * Merge two copies of a sticky referral marker.
 *
 * `true` always wins, in whichever copy it is found, and neither `false` nor a
 * missing value can ever replace it. Both markers describe something that HAS
 * happened and cannot un-happen: an invite was claimed; the server recorded the
 * training flag. So the direction of the merge is the direction of the truth.
 *
 * Anything that is not the boolean `true` counts as `false`. The value round-trips
 * through a synced JSON document, so a hand-edited or half-written entry really
 * can turn up, and treating a stray string as "yes" would arm a call for somebody
 * nobody invited.
 */
export function mergeStickyReferralFlag(mine: unknown, theirs: unknown): boolean {
  return mine === true || theirs === true;
}
